import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, GitCompareArrows, ArrowUp, ArrowDown, Minus } from "lucide-react";
import api from "../lib/api";
import { useT } from "../context/LanguageContext";
import TopBar from "../components/TopBar";

export default function Compare() {
  const { examId } = useParams();
  const navigate = useNavigate();
  const t = useT();
  const [hist, setHist] = useState(null);
  const [a, setA] = useState(0);
  const [b, setB] = useState(1);

  useEffect(() => {
    api.get(`/exams/${examId}/simulations`).then((r) => {
      const h = r.data.history.filter((s) => s.per_topic && s.per_topic.length);
      setHist(h);
      if (h.length >= 2) { setA(h.length - 2); setB(h.length - 1); }
    }).catch(() => setHist([]));
  }, [examId]);

  if (!hist) return <div className="space-bg min-h-screen flex items-center justify-center text-white/60">{t("Caricamento...")}</div>;

  if (hist.length < 2) return (
    <div className="space-bg min-h-screen">
      <TopBar />
      <div className="max-w-lg mx-auto px-4 pt-6 text-center">
        <button onClick={() => navigate(`/exam/${examId}`)} className="flex items-center gap-1 text-white/60 bouncy mb-4"><ChevronLeft size={18} /> {t("Esame")}</button>
        <GitCompareArrows size={40} style={{ color: "var(--cyan)" }} className="mx-auto my-6" />
        <p className="text-white/60" data-testid="compare-need-more">{t("Servono almeno 2 simulazioni completate per il confronto. Fanne un'altra!")}</p>
      </div>
    </div>
  );

  const simA = hist[a], simB = hist[b];
  const label = (s, i) => `#${i + 1} · ${s.voto}/30`;
  const topics = {};
  (simA.per_topic || []).forEach((t) => { topics[t.topic] = { a: t.score, b: null }; });
  (simB.per_topic || []).forEach((t) => { topics[t.topic] = { ...(topics[t.topic] || { a: null }), b: t.score }; });

  return (
    <div className="space-bg min-h-screen pb-16">
      <TopBar />
      <div className="max-w-lg mx-auto px-4 pt-5">
        <button onClick={() => navigate(`/exam/${examId}`)} className="flex items-center gap-1 text-white/60 bouncy mb-4" data-testid="compare-back-btn"><ChevronLeft size={18} /> {t("Esame")}</button>
        <h1 className="font-heading font-black text-2xl tracking-tight flex items-center gap-2 mb-5"><GitCompareArrows style={{ color: "var(--cyan)" }} /> {t("Confronto simulazioni")}</h1>

        <div className="grid grid-cols-2 gap-3 mb-6">
          <div>
            <label className="text-xs text-white/50">{t("Prima")}</label>
            <select value={a} onChange={(e) => setA(+e.target.value)} className="w-full bg-black/40 rounded-xl px-3 py-2 mt-1 outline-none" data-testid="compare-select-a">
              {hist.map((s, i) => <option key={i} value={i} className="bg-gray-900">{label(s, i)}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-white/50">{t("Dopo")}</label>
            <select value={b} onChange={(e) => setB(+e.target.value)} className="w-full bg-black/40 rounded-xl px-3 py-2 mt-1 outline-none" data-testid="compare-select-b">
              {hist.map((s, i) => <option key={i} value={i} className="bg-gray-900">{label(s, i)}</option>)}
            </select>
          </div>
        </div>

        <div className="glass rounded-2xl p-4 mb-5 flex items-center justify-around text-center" data-testid="compare-overall">
          <div><div className="font-num text-3xl">{simA.voto}</div><div className="text-xs text-white/40">/30</div></div>
          {(() => { const dv = simB.voto - simA.voto; const c = dv > 0 ? "var(--success)" : dv < 0 ? "var(--danger)" : "var(--gold)"; const I = dv > 0 ? ArrowUp : dv < 0 ? ArrowDown : Minus;
            return <div className="flex items-center gap-1 font-num font-bold" style={{ color: c }}><I size={20} /> {dv > 0 ? "+" : ""}{dv}</div>; })()}
          <div><div className="font-num text-3xl" style={{ color: "var(--cyan)" }}>{simB.voto}</div><div className="text-xs text-white/40">/30</div></div>
        </div>

        <h3 className="font-heading font-bold text-sm mb-2">{t("Per argomento")}</h3>
        <div className="space-y-2">
          {Object.entries(topics).map(([topic, v], i) => {
            const delta = (v.b ?? 0) - (v.a ?? 0);
            const c = delta > 0 ? "var(--success)" : delta < 0 ? "var(--danger)" : "var(--gold)";
            const I = delta > 0 ? ArrowUp : delta < 0 ? ArrowDown : Minus;
            return (
              <div key={i} className="glass rounded-2xl p-3" data-testid={`compare-topic-${i}`}>
                <div className="flex justify-between text-sm mb-1"><span>{topic}</span>
                  <span className="flex items-center gap-1 font-num" style={{ color: c }}><I size={14} /> {delta > 0 ? "+" : ""}{delta}</span>
                </div>
                <div className="flex gap-2 items-center text-xs font-num text-white/50">
                  <span>{v.a ?? "-"}%</span>
                  <div className="flex-1 h-2 rounded-full bg-black/40 relative overflow-hidden">
                    <div className="absolute h-full rounded-full opacity-40" style={{ width: `${v.a ?? 0}%`, background: "#888" }} />
                    <div className="absolute h-full rounded-full" style={{ width: `${v.b ?? 0}%`, background: c }} />
                  </div>
                  <span style={{ color: c }}>{v.b ?? "-"}%</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
